/**
 * FieldDraftPane — Right-side panel in workspace mode.
 *
 * Renders the versioned HTML draft using the Tiptap RichTextEditor.
 * Supports save (optimistic-locked) and publish-to-field.
 */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useFieldDraft } from '../hooks/useFieldDraft';
import { FIELDS_WITH_IMAGES } from '../configs/fieldConfig';
import RichTextEditor from './RichTextEditor';

interface FieldDraftPaneProps {
  subProjectId: string;
  field: string;
  fieldLabel: string;
  projectId?: string;
  solutionId?: string | null;
  onPublished?: (field: string, content: string) => void;
  reloadTrigger?: number;
  /** Optional export action shown alongside Save/Publish (e.g. Export as .docx for SOTA). */
  onExport?: () => Promise<void>;
  exportLabel?: string;
}

const FieldDraftPane: React.FC<FieldDraftPaneProps> = ({
  subProjectId,
  field,
  fieldLabel,
  projectId,
  solutionId,
  onPublished,
  reloadTrigger,
  onExport,
  exportLabel,
}) => {
  const { t } = useTranslation();
  const [publishMsg, setPublishMsg] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  const {
    draft, loading, saving, publishing, dirty, error,
    updateBody, save, publish, reload,
  } = useFieldDraft({ subProjectId, field, solutionId });

  useEffect(() => {
    if (reloadTrigger && reloadTrigger > 0) reload();
  }, [reloadTrigger, reload]);

  const handlePublish = async () => {
    const ok = await publish();
    if (ok) {
      setPublishMsg(t('workspace.draft.published', 'Published to field'));
      onPublished?.(field, draft?.body_html || '');
      setTimeout(() => setPublishMsg(null), 3000);
    }
  };

  const imagesEnabled = FIELDS_WITH_IMAGES.has(field) && !!projectId;

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-200 bg-slate-50 flex-shrink-0">
        <span className="text-xs font-semibold text-slate-600 flex-1">
          {fieldLabel}
          {dirty && <span className="ml-2 text-amber-500 text-[10px]">●</span>}
        </span>
        {draft && (
          <>
            {dirty && (
              <button
                onClick={save}
                disabled={saving}
                className="text-[10px] px-2 py-0.5 rounded bg-blue-600 text-white font-semibold disabled:opacity-50"
              >
                {saving ? t('common.saving', 'Saving...') : t('common.save', 'Save')}
              </button>
            )}
            {!dirty && !loading && (
              <button
                onClick={handlePublish}
                disabled={publishing}
                title={t('workspace.draft.publishTitle', 'Copy document content to sub-project field')}
                className="text-[10px] px-2 py-0.5 rounded border border-green-600 bg-green-50 text-green-700 font-semibold disabled:opacity-50"
              >
                {publishing ? '...' : t('workspace.draft.publish', 'Publish')}
              </button>
            )}
            {onExport && (
              <button
                onClick={async () => { setExporting(true); try { await onExport(); } finally { setExporting(false); } }}
                disabled={exporting}
                className="text-[10px] px-2 py-0.5 rounded border border-slate-300 text-slate-600 hover:border-blue-400 hover:text-blue-600 disabled:opacity-40"
              >
                {exporting ? '…' : (exportLabel || t('sota.export.button', 'Export as .docx'))}
              </button>
            )}
          </>
        )}
      </div>

      {/* Body */}
      <div className="flex-1 min-h-0 overflow-hidden">
        {loading && (
          <div className="p-3 text-xs text-slate-400">{t('common.loading', 'Loading...')}</div>
        )}
        {error && (
          <div className="p-3 text-xs text-red-600">{error}</div>
        )}
        {!loading && !draft && (
          <div className="p-3 text-xs text-slate-400">
            {t('workspace.draft.empty', 'No document yet. Start a conversation with the agent.')}
          </div>
        )}
        {draft && (
          <RichTextEditor
            content={draft.body_html}
            onChange={updateBody}
            editable={true}
            enableImages={imagesEnabled}
            projectId={projectId}
            placeholder={t('workspace.draft.placeholder', 'Start writing or ask the agent to generate content...')}
          />
        )}
      </div>

      {/* Footer */}
      {draft && (
        <div className="flex items-center gap-2 px-3 py-1 border-t border-slate-100 text-[10px] text-slate-400 flex-shrink-0">
          <span>v{draft.version}</span>
          <span>{new Date(draft.last_modified_at).toLocaleString()}</span>
          {publishMsg && <span className="text-green-600 font-semibold">{publishMsg}</span>}
        </div>
      )}
    </div>
  );
};

export default FieldDraftPane;
